import { Button, Card, type ChildrenProp, Icon, useUuid } from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";
import { useMemo, type FC } from "react";

import {
  type HotspotRecord,
  isWebGLSupported,
  MapboxMapView,
  type MapboxMapViewProps,
} from "../MapboxMapView";

import { buttonStyle, cardContentStyle, cardStyle } from "./MapboxMapCard.css";
import { elevations } from "@huub-web/ui/theme/css/elevations.css";

export type MapboxMapCardProps = MapboxMapViewProps &
  ChildrenProp & {
    onClick?: () => void;
    buttonValue?: string;
  };

export const MapboxMapCard: FC<MapboxMapCardProps> = ({
  center,
  buttonValue,
  onClick,
  children,
  ...props
}) => {
  const id = useUuid();

  const webGLSupported = isWebGLSupported();

  const hotspots = useMemo<HotspotRecord[]>(
    () => [{ id, type: "dot", lat: center[0], lng: center[1] }],
    [id, center],
  );

  return (
    <Card
      padding="none"
      overflowHidden
      className={cardStyle({ canHover: webGLSupported })}
      contentClassName={cardContentStyle}
      onClick={webGLSupported ? onClick : undefined}
    >
      <MapboxMapView
        {...props}
        className={mergeClasses("mapbox-map-card")}
        controlsConfig={false}
        hotspots={hotspots}
        center={center}
      />
      {buttonValue && webGLSupported && (
        <Button
          as="div"
          variant="background"
          className={mergeClasses(buttonStyle, elevations.m)}
          onClick={onClick}
          leading={<Icon icon="pin" size="s" />}
        >
          {buttonValue}
        </Button>
      )}
      {children}
    </Card>
  );
};
